import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	selectGlobalTimeuuid,
	selectGlobalLineName,
	selectGlobalSurveyType
} from '../../selectors/globalData';
import {
	selectIsACOnOff,
	selectFiles,
	selectReportType,
	selectSheetsIncluded,
	selectExtraExceptions,
	selectIncludePotentialGraph,
	selectPotentialYLimit,
	selectPotentialReferenceLines,
	selectWaveFiles,
	selectIncludeWaveGraph,
	selectWaveYLimit,
	selectWaveReferenceLines,
	selectEquipmentCalibration,
	selectOQReports,
	selectIncludeOQReports
} from './report.generator.slice';
import { getAccessToken } from '../../../../scanline/utils/token';
import { getUploadPolicySave } from '../../../utils/JobUtil/JubUtil.api';
import {
	createFormDataForSaveGenericFile,
	getWaveFiles as getWaveFilesAPI,
	getOQReports as getOQReportsAPI,
	getEquipmentCalibrations as getEquipmentCalibrationsAPI,
	uploadUsingJquery
} from '../../../utils/JobUtil/JobUtil.saveGpsReadings';
import getAipUrls from '../../../../config/getAipUrls';

const AIP_URLS = getAipUrls();

function delay(t, val) {
	return new Promise(resolve => setTimeout(resolve, t, val));
}

async function getReportStatus(executionId, counter = 0) {
	console.log('getReportStatus', counter);
	await delay(2000);
	const response = await fetch(
		`${AIP_URLS.reportgeneration}/machine/status?executionId=${executionId}`,
		{
			headers: {
				'Content-Type': 'application/json'
			}
		}
	);
	if (!response.ok) {
		throw new Error(`Response status: ${response.status}`);
	}
	const res = await response.json();
	console.log(counter, res);
	if (counter < 450 && res && res.status === 'RUNNING') {
		console.log('here');
		return await getReportStatus(executionId, counter + 1);
	}
	return res;
}

export const getWaveFiles = createAsyncThunk(
	'reportGenerator/getWaveFiles',
	async (args, { getState }) => {
		const state = getState();
		const job_id = selectGlobalTimeuuid(state);
		const waveFiles = await getWaveFilesAPI(job_id);
		return waveFiles.map(s3Path => ({
			s3Path,
			fileName: s3Path.substring(s3Path.lastIndexOf('/') + 1),
			include: true
		}));
	}
);

export const getOQReports = createAsyncThunk(
	'reportGenerator/getOQReports',
	async (args, { getState }) => {
		const state = getState();
		const job_id = selectGlobalTimeuuid(state);
		const oqReports = await getOQReportsAPI(job_id);
		return oqReports.map(s3Path => ({
			s3Path,
			fileName: s3Path.substring(s3Path.lastIndexOf('/') + 1),
			include: true
		}));
	}
);

export const getEquipmentCalibrations = createAsyncThunk(
	'reportGenerator/getEquipmentCalibrations',
	async (args, { getState }) => {
		const state = getState();
		const job_id = selectGlobalTimeuuid(state);
		const equipmentCalibrations = await getEquipmentCalibrationsAPI(job_id);
		return equipmentCalibrations.map(s3Path => ({
			s3Path,
			fileName: s3Path.substring(s3Path.lastIndexOf('/') + 1),
			include: true
		}));
	}
);

export const createReport = createAsyncThunk(
	'reportGenerator/createReport',
	async (args, { getState }) => {
		const state = getState();
		const job_id = selectGlobalTimeuuid(state);
		const lineName = selectGlobalLineName(state);
		const surveyType = selectGlobalSurveyType(state);
		const isACONOFF = selectIsACOnOff(state);
		const files = selectFiles(state);
		const report_type = selectReportType(state);
		const sheets_included = selectSheetsIncluded(state);
		const extra_exceptions = selectExtraExceptions(state);
		const includePotentialGraph = selectIncludePotentialGraph(state);
		const potentialYLimit = selectPotentialYLimit(state);
		const potentialReferenceLines = selectPotentialReferenceLines(state);
		const access_token = getAccessToken();
		const includeWaveGraphs = selectIncludeWaveGraph(state);
		const waveYLimit = selectWaveYLimit(state);
		const waveReferenceLines = selectWaveReferenceLines(state);
		const waveFiles = selectWaveFiles(state);
		const includeEquipmentCalibrations = selectEquipmentCalibration(state);
		const equipmentCalibrations = selectEquipmentCalibration(state);
		const includeOQReports = selectIncludeOQReports(state);
		const oqReports = selectOQReports(state);

		const wave_graph_config = !includeWaveGraphs
			? []
			: waveFiles.map(waveFile => {
					return {
						txt_path: waveFile.s3Path,
						y_limit: waveYLimit,
						reference_lines: waveReferenceLines
					};
			  });
		const oq_reports_png_paths = !includeOQReports
			? []
			: oqReports.map(oqReport => {
					return oqReport.s3Path;
			  });

		const equipment_calibrations_png_paths = !includeEquipmentCalibrations
			? []
			: equipmentCalibrations.map(equipmentCalibration => {
					return equipmentCalibration.s3Path;
			  });

		const message = {
			api_body: {
				lineName,
				surveyType,
				isACONOFF,
				fileNames: files
			},
			job_id,
			report_type,
			sheets_included,
			extra_exceptions: extra_exceptions.map(({ guid, ...e }) => e),
			potential_graph_config: includePotentialGraph
				? {
						y_limit: potentialYLimit,
						reference_lines: potentialReferenceLines
				  }
				: {},
			wave_graph_config: wave_graph_config,
			oq_reports_png_paths,
			equipment_calibrations_png_paths,
			access_token
		};
		const response = await fetch(`${AIP_URLS.reportgeneration}/machine/start`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(message)
		});

		if (!response.ok) {
			throw new Error(`Response status: ${response.status}`);
		}

		const json = await response.json();

		const { executionId } = json;

		const results = await getReportStatus(executionId);

		return results;
		// console.log(message, json);
	}
);

const uploadFile = async (
	file,
	jobguid,
	folder,
	contentType = 'text/plain'
) => {
	const policyData = await getUploadPolicySave(
		file.name,
		'000000',
		jobguid,
		folder
	);

	return new Promise((resolve, reject) => {
		if (file) {
			const reader = new FileReader();

			reader.onload = function(e) {
				const fileContent = e.target.result; // The content of the file

				const formData = createFormDataForSaveGenericFile(
					policyData,
					file.name,
					fileContent,
					file.type
				);

				uploadUsingJquery(policyData.upload_url, formData).then(() => {
					resolve({});
				}); // You can now use fileContent as needed (e.g., display in a <pre> tag)
			};

			reader.onerror = function(e) {
				console.error('Error reading file:', e);
				reject(e);
			};

			// Read the file as text (can also use readAsDataURL, readAsArrayBuffer, etc.)
			console.log(file);
			if (file.type === 'text/plain') {
				reader.readAsText(file);
			} else {
				reader.readAsArrayBuffer(file);
			}

			//reader.readAsDataURL(file);
		}
	});
};

export const uploadWaveFile = createAsyncThunk(
	'reportGenerator/uploadWaveFile',
	async (file, { getState, dispatch }) => {
		const state = getState();
		const jobguid = selectGlobalTimeuuid(state);
		await uploadFile(file, jobguid, 'txts');
		dispatch(getWaveFiles());
	}
);

export const uploadOQReport = createAsyncThunk(
	'reportGenerator/uploadOQReport',
	async (file, { getState, dispatch }) => {
		const state = getState();
		const jobguid = selectGlobalTimeuuid(state);
		await uploadFile(file, jobguid, 'oqreports', 'image/png');
		dispatch(getOQReports());
	}
);

export const uploadEquipmentCalibrations = createAsyncThunk(
	'reportGenerator/uploadEquipmentCalibrations',
	async (file, { getState, dispatch }) => {
		const state = getState();
		const jobguid = selectGlobalTimeuuid(state);
		await uploadFile(file, jobguid, 'equiqmentcalibrations', 'image/png');
		dispatch(getEquipmentCalibrations());
	}
);
